import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isPreviewMode } from "../api/client";
import { Icon, type IconName } from "./Icon";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  description: string;
}

const navigation: NavItem[] = [
  { to: "/overview", label: "概览", icon: "overview", description: "运行态势" },
  { to: "/accounts", label: "账号库", icon: "accounts", description: "列表与账号" },
  { to: "/tasks", label: "任务", icon: "tasks", description: "进度与历史" },
  { to: "/integrations", label: "集成", icon: "integrations", description: "Sub2 与代理" },
  { to: "/credits", label: "积分", icon: "credits", description: "余额与流水" },
];

const adminNavigation: NavItem[] = [
  { to: "/admin/overview", label: "管理总览", icon: "overview", description: "系统与队列态势" },
  { to: "/admin/users", label: "用户与积分", icon: "user", description: "身份、启停与流水" },
  { to: "/admin/codes", label: "卡密", icon: "key", description: "生成与库存管理" },
  { to: "/admin/tasks", label: "任务监管", icon: "tasks", description: "跨用户执行现场" },
  { to: "/admin/diagnostics", label: "运行诊断", icon: "pulse", description: "队列与一致性" },
];

const pageMeta: Record<string, { eyebrow: string; title: string }> = {
  "/admin/overview": { eyebrow: "CONTROL PLANE", title: "管理总览" },
  "/admin/users": { eyebrow: "IDENTITY & BALANCE", title: "用户与积分" },
  "/admin/codes": { eyebrow: "REDEMPTION INVENTORY", title: "卡密管理" },
  "/admin/tasks": { eyebrow: "EXECUTION CONTROL", title: "任务监管" },
  "/admin/diagnostics": { eyebrow: "OBSERVABILITY", title: "运行诊断" },
  "/overview": { eyebrow: "今日工作台", title: "运行概览" },
  "/accounts": { eyebrow: "数据资产", title: "账号库" },
  "/tasks": { eyebrow: "执行中心", title: "任务" },
  "/integrations": { eyebrow: "连接与路由", title: "集成" },
  "/credits": { eyebrow: "账户资产", title: "积分" },
};

function NavigationLink({ item, mobile = false }: { item: NavItem; mobile?: boolean }) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => `${mobile ? "mobile-nav__link" : "side-nav__link"}${isActive ? " is-active" : ""}`}
    >
      <Icon name={item.icon} size={mobile ? 21 : 20} />
      <span>
        <b>{item.label}</b>
        {!mobile ? <small>{item.description}</small> : null}
      </span>
    </NavLink>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const rootPath = `/${location.pathname.split("/")[1] || "overview"}`;
  const isAdminArea = rootPath === "/admin";
  const adminPath = `/${location.pathname.split("/").slice(1, 3).join("/")}`;
  const meta = pageMeta[isAdminArea ? adminPath : rootPath] || { eyebrow: "OA Flow", title: "工作台" };
  const activeNavigation = isAdminArea ? adminNavigation : navigation;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <aside className="sidebar" aria-label="主导航">
        <NavLink to={isAdminArea ? "/admin/overview" : "/overview"} className="brand-lockup" aria-label="OA Flow">
          <span className="brand-mark"><span /></span>
          <span><b>OA FLOW</b><small>REAUTH OPERATIONS</small></span>
        </NavLink>
        <p className="side-label">{isAdminArea ? "管理控制台" : "工作空间"}</p>
        <nav className="side-nav" aria-label={isAdminArea ? "管理控制台" : "工作空间"}>
          {activeNavigation.map((item) => <NavigationLink key={item.to} item={item} />)}
        </nav>
        {user?.role === "admin" ? <NavLink className="admin-mode-switch" to={isAdminArea ? "/overview" : "/admin/overview"}>{isAdminArea ? "返回用户工作台" : "进入管理控制台"}<Icon name="arrow" size={16} /></NavLink> : null}
        <div className="sidebar__signal">
          <span className="signal-orbit"><span /></span>
          <div><b>会话已连接</b><small>任务状态自动恢复</small></div>
        </div>
        <div className="sidebar__user">
          <span className="avatar">{user?.username.slice(0, 1).toUpperCase()}</span>
          <div><b>{user?.username}</b><small>{user?.role === "admin" ? "管理员" : "操作员"}</small></div>
          <button className="icon-button icon-button--dark" onClick={() => void logout()} aria-label="退出登录" title="退出登录">
            <Icon name="logout" size={18} />
          </button>
        </div>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <div>
            <span className="eyebrow">{meta.eyebrow}</span>
            <h1>{meta.title}</h1>
          </div>
          <div className="topbar__actions">
            {isPreviewMode ? <span className="preview-badge">只读预览</span> : null}
            {isAdminArea ? (
              <NavLink to="/admin/diagnostics" className="balance-chip" aria-label="管理控制台运行诊断">
                <span>管理模式</span><b>ADMIN</b>
              </NavLink>
            ) : (
              <NavLink to="/credits" className="balance-chip" aria-label={`当前积分 ${user?.credit_balance ?? 0}`}>
                <span>可用积分</span><b>{user?.credit_balance ?? 0}</b>
              </NavLink>
            )}
            <span className="topbar__avatar">{user?.username.slice(0, 1).toUpperCase()}</span>
          </div>
        </header>
        <main id="main-content" className="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      <nav className="mobile-nav" aria-label="移动端主导航">
        {activeNavigation.map((item) => <NavigationLink key={item.to} item={item} mobile />)}
      </nav>
    </div>
  );
}
